import type { ReactElement } from 'react'

import styles from './RacionadoSettingsPage.module.scss'

/** Three marks as typed: what a field holds until it parses. */
export type MarkTexts = [string, string, string]

const INTEGER = /^\d+$/

/** A mark's minutes or hours, or null while what is typed is not a whole number. */
export const markValue = (text: string): number | null => (INTEGER.test(text.trim()) ? Number(text.trim()) : null)

type ThresholdRulerProps = {
  /** The alarm («Para abatir»): it prefixes every control's name, so two rulers never share one. */
  name: string
  unit: 'min' | 'h'
  /** What each mark turns on, lowest first: «!», «!!», «!!!». */
  marks: readonly [string, string, string]
  values: MarkTexts
  onChange: (values: MarkTexts) => void
  disabled: boolean
  /** Where the ruler ends, in `unit`; it grows past the highest mark. */
  scale: number
  /** The lowest mark is the most urgent (the heat, by the minutes left to the target): its colours run the other way. */
  urgentFirst?: boolean
}

/**
 * The three marks of an alarm on a ruler, lowest first. Each mark is a
 * slider — dragged, or moved with the arrow keys — that stops at its
 * neighbours, so the ruler can never put them out of order; and a field to
 * type it exactly. A typed mark out of order is painted so, and the page
 * refuses to save it.
 */
export const ThresholdRuler = ({ name, unit, marks, values, onChange, disabled, scale, urgentFirst = false }: ThresholdRulerProps): ReactElement => {
  const parsed = values.map(markValue)
  const end = Math.max(scale, Math.ceil(Math.max(...parsed.map((value) => value ?? 0)) * 1.2))
  const inOrder = parsed.every((value, index) => index === 0 || value === null || (parsed[index - 1] ?? 0) <= value)
  const at = (index: number): number => parsed[index] ?? 0
  const percent = (value: number): string => `${Math.min(100, (value / end) * 100)}%`
  // The colour step of a mark: 1 the mildest, 3 the most urgent.
  const step = (index: number): number => (urgentFirst ? 3 - index : index + 1)

  const set = (index: number, text: string): void => {
    const next: MarkTexts = [...values]
    next[index] = text
    onChange(next)
  }

  const slide = (index: number, value: number): void => {
    const low = index === 0 ? 0 : at(index - 1)
    const high = index === 2 ? end : at(index + 1)
    set(index, String(Math.min(high, Math.max(low, value))))
  }

  return (
    <div className={styles.ruler} role="group" aria-label={name}>
      <div className={`${styles.ruler__track} ${inOrder ? '' : styles['ruler__track--wrong']}`}>
        {[0, 1, 2].map((index) => (
          <span
            key={`band-${index}`}
            className={`${styles.ruler__band} ${styles[`ruler__band--${step(index)}`]}`}
            style={urgentFirst
              ? { left: index === 0 ? '0%' : percent(at(index - 1)), right: `calc(100% - ${percent(at(index))})` }
              : { left: percent(at(index)), right: index === 2 ? '0%' : `calc(100% - ${percent(at(index + 1))})` }}
          />
        ))}
        {[0, 1, 2].map((index) => (
          <input
            key={`slider-${index}`}
            type="range"
            className={styles.ruler__slider}
            min={0}
            max={end}
            step={1}
            value={at(index)}
            disabled={disabled}
            aria-label={`${name}: ${marks[index]} en la regla`}
            aria-valuetext={`${at(index)} ${unit}`}
            onChange={(event) => slide(index, Number(event.target.value))}
          />
        ))}
      </div>
      <div className={styles.ruler__scale} aria-hidden="true">
        <span>0</span>
        <span>{`${end} ${unit}`}</span>
      </div>
      <div className={styles.ruler__fields}>
        {[0, 1, 2].map((index) => (
          <label key={`field-${index}`} className={styles.ruler__field}>
            <span className={`${styles.ruler__mark} ${styles[`ruler__mark--${step(index)}`]}`}>{marks[index]}</span>
            <span>a</span>
            <input
              className={styles.inline}
              inputMode="numeric"
              value={values[index]}
              disabled={disabled}
              aria-label={`${name}: ${marks[index]}`}
              aria-invalid={parsed[index] === null || !inOrder}
              onChange={(event) => set(index, event.target.value)}
            />
            <span>{unit}</span>
          </label>
        ))}
      </div>
    </div>
  )
}
